<template>
  <ui-dialog
    :show="show"
    max-width="1150px"
    @input="closeDialog"
    custom-class="facilities-dialog"
  >
    <template #header>
      <div class="d-flex flex-row align-center justify-space-between">
        <h3>
          {{ $t("message.reviews.categories.selectLocations") }}
        </h3>
      </div>
    </template>
    <div class="facilities-dialog-body">
      <v-tabs v-model="tab" slider-color="primary" class="tabs">
        <v-tab :ripple="false" class="tabs__item">
          {{ $t("message.common.all") }}
        </v-tab>
        <v-tab :ripple="false" class="tabs__item">
          {{ $t("message.common.city") }}
        </v-tab>
        <v-tab :ripple="false" class="tabs__item">
          {{ $t("message.reviews.categories.locationsGroup") }}
        </v-tab>
        <v-tab :ripple="false" class="tabs__item">
          {{ $t("message.common.facility") }}
        </v-tab>
      </v-tabs>
      <v-tabs-items class="tabs__content" v-model="tab">
        <v-tab-item :transition="false">
          <div class="all-facilities">
            <div class="all-facilities-header">
              {{
                $t(
                  "message.reviews.categories.facilityDialog.allFacilitiesTitle"
                )
              }}
            </div>
            <div class="company-info">
              <div class="company-name">{{ companyName }}</div>
              <div class="company-facilities-count">
                ({{ getFacilitiesNumberLabel }})
              </div>
            </div>
          </div>
        </v-tab-item>
        <v-tab-item :transition="false">
          <FacilitiesDialogList
            :search-placeholder="
              $t('message.reviews.categories.citySearchPlaceholder')
            "
            :tab="tab"
            :selected-id="selectedTab.id"
            :loader="loader"
            :items="filteredCities"
            @search-value="searchCities"
            @select-item="selectItemHandler('city', $event)"
          />
        </v-tab-item>
        <v-tab-item :transition="false">
          <FacilitiesDialogList
            :search-placeholder="
              $t('message.reviews.categories.groupSearchPlaceholder')
            "
            :tab="tab"
            :items="results"
            :page="page"
            :per-page="perPage"
            :loader="loader"
            :selected-id="selectedTab.id"
            :total-count="totalCount"
            @search-value="(page = 1), fetchFacilityGroups($event)"
            @select-item="selectItemHandler('facility_group', $event)"
            @change="changeGroupPage"
          />
        </v-tab-item>
        <v-tab-item :transition="false">
          <FacilitiesDialogList
            :search-placeholder="
              $t('message.reviews.categories.facilitySearchPlaceholder')
            "
            :tab="tab"
            :items="results"
            :page="page"
            :per-page="perPage"
            :total-count="totalCount"
            :loader="loader"
            show-address
            :selected-id="selectedTab.id"
            @search-value="(page = 1), fetchFacilities($event)"
            @select-item="selectItemHandler('facility', $event)"
            @change="changeFacilityPage"
          />
        </v-tab-item>
      </v-tabs-items>
      <SectionLoader :status="loader" />
    </div>

    <template #actions>
      <div class="facilities-dialog__footer">
        <ui-button
          color="primary"
          :disabled="!Object.keys(selectedOption).length && tab !== 0"
          @click="confirmationHandler"
        >
          {{ $t("message.common.choose") }}
        </ui-button>
      </div>
    </template>
  </ui-dialog>
</template>

<script>
import { pluralCount } from "@/helpers/helpers";
import FacilitiesDialogList from "@/components/Reviews/Categories/FacilitiesDialogList.vue";
import { mapState } from "vuex";
import facilitiesService from "@/services/facilities";
import serviceHandler from "@/helpers/handlers/service";
import facilitiesGroupsService from "@/services/facilitiesGroups";
import SectionLoader from "@/components/Loader/SectionLoader.vue";

export default {
  name: "FacilitiesDialog",
  components: {
    FacilitiesDialogList,
    SectionLoader
  },
  props: {
    show: {
      type: Boolean,
      default: true
    },
    selectedTab: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      tab: null,
      selectedOption: {},
      filteredCities: [],
      results: [],
      loader: false,
      totalCount: 0,
      page: 1,
      perPage: 10
    };
  },
  computed: {
    ...mapState({
      currentPageFacilitiesGroups: state =>
        state.facilitiesGroups.currentPageFacilitiesGroups,
      companyName: state => state.company.company.name,
      facilitiesCount: state => state.company.company.facilities_count
    }),
    getFacilitiesNumberLabel() {
      return pluralCount("message.pluralForms.facility", this.facilitiesCount);
    }
  },

  watch: {
    selectedTab: {
      immediate: true,
      handler() {
        this.selectedOption = this.selectedTab;
        this.tab = this.selectedOption.tab;
      }
    },
    tab: {
      immediate: true,
      handler(current) {
        this.results = [];
        this.selectedOption = {};
        this.page = 1;

        switch (current) {
          case 0:
            this.selectedOption = {};
            break;
          case 1:
            this.initCities({});
            break;
          case 2:
            this.fetchFacilityGroups({});
            break;
          case 3:
            this.fetchFacilities({});
            break;
          default:
            break;
        }
      }
    }
  },

  methods: {
    pageParams(searchValue) {
      return {
        ...(searchValue &&
          typeof searchValue === "string" && {
            query: searchValue
          }),
        page: this.page,
        perPage: this.perPage
      };
    },
    searchCities(e) {
      if (!e.searchValue) {
        this.filteredCities = this.results;
        return;
      }
      this.filteredCities = this.results.filter(
        city => !!city.name.toLowerCase().includes(e.searchValue.toLowerCase())
      );
    },

    async changeFacilityPage(e) {
      this.page = e.page;

      await this.fetchFacilities(e);
    },
    async changeGroupPage(e) {
      this.page = e.page;

      await this.fetchFacilityGroups(e);
    },
    closeDialog() {
      this.$emit("close");
    },
    selectItemHandler(type, data) {
      this.selectedOption = {
        id: type === "city" ? data.name : data.id,
        params: {
          [type]: type === "city" ? data.name : data.id,
          ...(type === "facility" && {
            facilityGmb: data.facility_listings.address_group.gmb_address_id
          }),
          withoutCategory: this.$route.query.withoutCategory
        },
        meta: {
          type,
          ...(type === "facility"
            ? {
                displayName: data.name,
                storeCode: data.store_code,
                address: data.formatted_address
              }
            : { displayName: data.name }),
          quantity: data.facilities_count || 0
        },
        tab: data.tab
      };
    },
    confirmationHandler() {
      this.$emit("selected", this.selectedOption);
    },
    async initCities() {
      this.loader = true;

      await this.fetchCities();
      this.loaders = false;
    },
    async fetchData(callback) {
      this.loader = true;
      const { results, count } = await callback();
      this.results = results;
      this.totalCount = count;
      this.loader = false;
    },
    async fetchCities() {
      this.loader = true;
      const result = await serviceHandler(
        facilitiesService.getFacilitiesCities(this.facilitiesCitiesList)
      );
      this.results = result.map(({ city }, index) => {
        return { id: city + index, name: city };
      });
      this.filteredCities = this.results;
      this.totalCount = result.length;
      this.loader = false;
    },

    fetchFacilities(e) {
      this.fetchData(() => {
        return serviceHandler(
          facilitiesService.getFacilitiesData(this.pageParams(e.searchValue))
        );
      });
    },
    fetchFacilityGroups(e) {
      this.fetchData(() => {
        return serviceHandler(
          facilitiesGroupsService.getFacilitiesGroups(
            this.pageParams(e.searchValue)
          )
        );
      });
    }
  }
};
</script>
<style lang="scss" scoped>
@import "~vuetify/src/styles/settings/_variables.scss";

.facilities-dialog {
  &__footer {
    display: flex;
    justify-content: flex-end;
    width: 100%;
  }
}
.search-field {
  box-shadow: 0px 0px 30px rgba(54, 60, 86, 0.07);
}

.all-facilities {
  display: flex;
  flex-direction: column;
  align-items: center;
  font-weight: 500;
  @include font-size(16px);
  line-height: 20px;
  text-align: center;
  margin-top: $spacing * 10;
  height: 500px;

  .all-facilities-header {
    color: rgba($text, 0.9);
    margin-bottom: $spacing * 2.5;
  }

  .company-name {
    color: $primary;
    margin-bottom: $spacing * 2;
    @include font-size(24px);
  }

  .company-facilities-count {
    color: rgba($text, 0.5);
    @include font-size(12px);
    line-height: 15px;
    font-weight: normal;
    margin-bottom: $spacing * 6;
  }
}
.tabs {
  position: relative;
  z-index: 1;
  margin-top: $spacing * 2.5;
  border-bottom: 1px solid #ececec;
  border-top: 1px solid #ececec;
  &__item {
    width: 100%;
    text-transform: unset;
    letter-spacing: unset;
    font-weight: 500;
    padding: $spacing * 1.5 $spacing * 3;

    &.v-tab--active {
      background: white;
      overflow: hidden;
      color: $grey-darker;
    }
  }

  &__content {
    padding: $spacing * 2;
    &::v-deep.v-item-group {
      padding-bottom: 0 !important;
    }
  }
}
</style>
